import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import type { VehicleDetails } from "@/types/vehicleDetails";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";

interface AutoUncleVerificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (engineVolume?: string) => void;
  initialEngineVolume?: string;
  showEngineVolume: boolean;
  vehicleDetails: Partial<VehicleDetails>;
}

export const AutoUncleVerificationDialog = ({
  open,
  onClose,
  onSave,
  initialEngineVolume,
  showEngineVolume,
  vehicleDetails,
}: AutoUncleVerificationDialogProps) => {
  const { language } = useLanguage();
  const L = listingPostLabels(language);
  // Convert engineVolume string to number for slider (default to 1.0 if invalid)
  const parseEngineVolume = (value: string | undefined): number => {
    if (!value) return 1.0;
    const num = parseFloat(value);
    if (isNaN(num) || num < 0.5) return 0.5;
    if (num > 10) return 10;
    return num;
  };

  const [engineVolume, setEngineVolume] = useState<number>(
    parseEngineVolume(initialEngineVolume),
  );
  const [inputValue, setInputValue] = useState<string>(
    initialEngineVolume
      ? parseEngineVolume(initialEngineVolume).toFixed(1)
      : "1.0",
  );
  const [hasChanges, setHasChanges] = useState(false);

  // Update state when initial values change (when dialog opens)
  useEffect(() => {
    if (open) {
      const parsed = parseEngineVolume(initialEngineVolume);
      setEngineVolume(parsed);
      setInputValue(parsed.toFixed(1));
      setHasChanges(false);
    }
  }, [open, initialEngineVolume]);

  // Track changes
  useEffect(() => {
    const initialEngineVolumeNum = parseEngineVolume(initialEngineVolume);
    const engineVolumeChanged =
      Math.abs(engineVolume - initialEngineVolumeNum) > 0.01; // Allow small floating point differences
    setHasChanges(engineVolumeChanged);
  }, [engineVolume, initialEngineVolume]);

  // Handle input change - convert comma to period and validate
  const handleInputChange = (value: string) => {
    // Replace comma with period
    const normalizedValue = value.replace(",", ".");

    // Allow empty string, numbers, and one decimal point
    if (normalizedValue === "" || /^\d*\.?\d*$/.test(normalizedValue)) {
      setInputValue(normalizedValue);

      // Parse and update engineVolume if valid
      const num = parseFloat(normalizedValue);
      if (!isNaN(num) && normalizedValue !== "") {
        const clamped = Math.max(0.5, Math.min(10, num));
        setEngineVolume(clamped);
        // Update input to show clamped value if it was out of range
        if (num !== clamped) {
          setInputValue(clamped.toFixed(1));
        }
      }
    }
  };

  // Handle input blur - format to one decimal place
  const handleInputBlur = () => {
    const num = parseEngineVolume(inputValue);
    setEngineVolume(num);
    setInputValue(num.toFixed(1));
  };

  const handleSave = () => {
    onSave(showEngineVolume ? engineVolume.toFixed(1) : undefined);
    onClose();
  };

  const handleClose = () => {
    // Reset to initial values if user closes without saving
    const parsed = parseEngineVolume(initialEngineVolume);
    setEngineVolume(parsed);
    setInputValue(parsed.toFixed(1));
    setHasChanges(false);
    onClose();
  };

  const nestedSheetProps = nativeNestedSheetDialogProps(open);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        {...nestedSheetProps}
        className={cn(nestedSheetProps.className, "sm:max-w-[500px]")}
      >
        <DialogHeader>
          <DialogTitle>
            {L.verification?.verifyVehicleData ?? "Verifiera fordonsdata"}
          </DialogTitle>
          <DialogDescription>
            {L.verification?.verifyDescription ??
              "Verifiera följande information, eller ändra om det ej stämmer:"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {showEngineVolume && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-medium text-sm">
                  {L.verification?.engineVolume ?? "Motorvolym (liter)"}
                </h3>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onBlur={handleInputBlur}
                    className="w-20 h-8 text-right text-sm font-semibold text-gray-900 dark:text-gray-100"
                    style={{ fontSize: "0.875rem" }}
                  />
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    L
                  </span>
                </div>
              </div>
              <Slider
                value={[
                  typeof engineVolume === "number"
                    ? engineVolume
                    : parseEngineVolume(String(engineVolume)),
                ]}
                onValueChange={(value) => {
                  const newValue = value[0];
                  setEngineVolume(newValue);
                  setInputValue(newValue.toFixed(1));
                }}
                min={0.5}
                max={10}
                step={0.1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0.5 L</span>
                <span>10.0 L</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className={
              hasChanges
                ? ""
                : "bg-green-600 hover:bg-green-500 text-white border-green-600"
            }
          >
            {hasChanges
              ? (L.widget?.cancel ?? "Avbryt")
              : (L.verification?.looksGood ?? "Ser bra ut")}
          </Button>
          {hasChanges && (
            <Button onClick={handleSave} className="rounded-full">
              {L.verification?.saveChanges ?? "Spara ändringar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
